import Vue from 'vue'
import { parseAxiosException } from '@/helpers/exceptionParser.js'
import PlanboardApiModel from './models/PlanboardApiModel'
import { format, endOfDay } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { filter, find, pipe, propEq } from 'ramda'

const buildModule = apiClient => ({
  namespaced: true,

  state: {
    planboard: null,
    selectedDate: null,
    apiResponse: null,
    isFetching: false,
    fetchError: null,
    isFetchingBooking: false,
    fetchBookingError: null,
    isSaving: false,
    saveError: null,
    dragInProgress: false,
    showOverview: false,
    activeBooking: null,
    activeBookingProduct: null,
    bookings: [],
  },

  mutations: {
    setPlanboard: (state, payload) => Vue.set(state, 'planboard', payload),
    setIsFetching: (state, payload) => (state.isFetching = payload),
    setFetchError: (state, payload) => (state.fetchError = payload),
    setIsFetchingBooking: (state, payload) => (state.isFetchingBooking = payload),
    setFetchBookingError: (state, payload) => (state.fetchBookingError = payload),
    setApiResponse: (state, payload) => (state.apiResponse = payload),
    setIsSaving: (state, payload) => (state.isSaving = payload),
    setSaveError: (state, payload) => (state.saveError = payload),
    setSelectedDate: (state, payload) => (state.selectedDate = payload),
    setDragInProgress: (state, payload) => (state.dragInProgress = payload),
    setOverview: (state, payload) => (state.showOverview = payload),
    addBooking: (state, payload) => state.bookings.push(payload),
    removeBooking: (state, bookingIndexToRemove) => state.bookings.splice(bookingIndexToRemove, 1),
    setActiveBooking: (state, payload) => (state.activeBooking = payload),
    setActiveBookingProduct: (state, payload) => (state.activeBookingProduct = payload),
  },

  actions: {
    async fetchPlanboardData({ commit, state }) {
      const postData = {
        Date: state.selectedDate,
      }
      commit('setIsFetching', true)
      commit('setFetchError', null)
      try {
        const response = await apiClient.post('planboard', postData)
        commit('setApiResponse', response)
        const model = new PlanboardApiModel(response.data)
        commit('setPlanboard', model)
      } catch (e) {
        console.error(e)
        commit('setFetchError', {
          rawError: e,
          parsedError: parseAxiosException(e),
        })
      }
      commit('setIsFetching', false)
    },
    setSelectedDate({ commit }, data) {
      const selectedDate = format(data, 'yyyy-MM-dd')
      commit('setSelectedDate', selectedDate)
    },
    setDragInProgress({ commit }, data) {
      commit('setDragInProgress', data)
    },
    moveBookingCardToTimeslot({ commit, state }, data) {
      const {
        bookingProductKey,
        fromResourceIndex,
        toResourceIndex,
        fromTimeslotIndex,
        toTimeslotIndex,
      } = data
      if (isMovingCardIsHappeningOnSameSpot(data)) return

      const planboard = state.planboard

      // move the bookingProduct object to the new location if moving trough different columns
      if (fromResourceIndex != toResourceIndex) {
        const fromBookingProducts = planboard.Resources[fromResourceIndex].BookingProducts
        const bookingProduct = find(propEq('BookingProductKey', bookingProductKey))(
          fromBookingProducts,
        )

        planboard.Resources[toResourceIndex].BookingProducts.push(bookingProduct)
      }

      // add the bookingProductKey to the new location
      const toTimeslot = planboard.Resources[toResourceIndex].Timeslots[toTimeslotIndex]
      if (toTimeslot.BookedProductKeys === null) {
        toTimeslot.BookedProductKeys = []
      }
      toTimeslot.BookedProductKeys.push(bookingProductKey)

      // remove the bookingProductKey to the old location
      const fromTimeslot = planboard.Resources[fromResourceIndex].Timeslots[fromTimeslotIndex]

      const removedItems = filter(item => item !== bookingProductKey)(
        fromTimeslot.BookedProductKeys,
      )

      fromTimeslot.BookedProductKeys = removedItems

      // update the time
      const toBookingProducts = planboard.Resources[toResourceIndex].BookingProducts
      const bookingProduct = find(propEq('BookingProductKey', bookingProductKey))(toBookingProducts)
      bookingProduct.StartTime = toTimeslot.StartTime
      bookingProduct.EndTime = toTimeslot.EndTime

      commit('setPlanboard', planboard)
    },
    async fetchBookingProduct({ state, commit }, bookingData) {
      commit('setOverview', true)
      commit('setIsFetchingBooking', true)
      commit('setFetchBookingError', null)
      const { BookingKey, BookingProductKey } = bookingData
      const booking = state.bookings?.find(o => o.Booking.Key === BookingKey)
      if (!booking) {
        try {
          const response = await apiClient.get(`bookings/${BookingKey}/slidein`)
          commit('addBooking', response.data)
          commit('setActiveBooking', response.data)
        } catch (e) {
          console.error(e)
          commit('setFetchBookingError', {
            rawError: e,
            parsedError: parseAxiosException(e),
          })
        }
      } else {
        commit('setActiveBooking', booking)
      }
      commit('setActiveBookingProduct', BookingProductKey)
      commit('setIsFetchingBooking', false)
    },
    async markAsCheckedIn({ state, commit, getters }) {
      commit('setIsFetchingBooking', true)
      let bookingIndexToRemove = null
      let updateCheckIn = false

      state.bookings?.forEach((object, index) => {
        if (object.Booking.Key === getters.getActiveBooking.Booking.Key)
          bookingIndexToRemove = index
      })

      if (bookingIndexToRemove != null) {
        try {
          const url = `bookings/${getters.getActiveBooking.Booking.Key}/attendance/CheckedIn`
          await apiClient.post(url)
          commit('removeBooking', bookingIndexToRemove)
          // update planboardData
          getters.getResources.map(resource =>
            resource.BookingProducts.map(bookingProduct => {
              if (bookingProduct.BookingKey === getters.getActiveBooking.Booking.Key)
                bookingProduct.BookingCheckedIn = true
            }),
          )
          updateCheckIn = true
        } catch (e) {
          console.error(e)
          commit('setFetchBookingError', {
            rawError: e,
            parsedError: parseAxiosException(e),
          })
        }
      }

      commit('setIsFetchingBooking', false)
      return updateCheckIn
    },
    hideOverview({ commit, state }) {
      if (state.showOverview) {
        commit('setActiveBooking', null)
        commit('setActiveBookingProduct', null)
        commit('setOverview', false)
      }
    },
  },

  getters: {
    isPending: state => state.isSaving || state.isFetching,
    isFetching: state => state.isFetching,
    isFetchingBooking: state => state.isFetchingBooking,
    isSaving: state => state.isSaving,
    planboardData: state => state.planboard,
    dragInProgress: state => state.dragInProgress,
    isShownOverview: state => state.showOverview,
    getActiveBooking: state => state.activeBooking,
    getActiveBookingProduct: state => state.activeBookingProduct,
    getResources: state => state.planboard.Resources,
  },
})

const isMovingCardIsHappeningOnSameSpot = data => {
  return (
    data.fromResourceIndex == data.toResourceIndex && data.fromTimeslotIndex == data.toTimeslotIndex
  )
}

export default buildModule
